import { Autocomplete as AutocompletePrimitive } from '@base-ui/react/autocomplete';
import { css } from 'aphrodite';
import * as React from 'react';
import { XIcon } from '../../components/svgs/icons/XIcon';
import { s } from './styles';

function Autocomplete({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Root>) {
	return <AutocompletePrimitive.Root data-slot='autocomplete' {...props} />;
}

function AutocompleteValue({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Value>) {
	return <AutocompletePrimitive.Value data-slot='autocomplete-value' {...props} />;
}

function AutocompleteControl({ styles = [], ...props }: AphroditeStyled<React.ComponentProps<'div'>>) {
	return <span data-slot='autocomplete-control' className={css(s.control, ...styles)} {...props} />;
}

function AutocompleteTrigger({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Trigger>) {
	return <AutocompletePrimitive.Trigger data-slot='autocomplete-trigger' {...props} />;
}

function AutocompleteInput({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Input>>) {
	return <AutocompletePrimitive.Input data-slot='autocomplete-input' className={css(s.input, ...styles)} {...props} />;
}

function AutocompleteIcon({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Icon>>) {
	return <AutocompletePrimitive.Icon data-slot='autocomplete-icon' className={css(s.icon, ...styles)} {...props} />;
}

function AutocompleteStatus({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Status>>) {
	return (
		<AutocompletePrimitive.Status data-slot='autocomplete-status' className={css(s.status, ...styles)} {...props} />
	);
}

function AutocompletePortal({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Portal>) {
	return <AutocompletePrimitive.Portal data-slot='autocomplete-portal' {...props} />;
}

function AutocompleteBackdrop({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Backdrop>) {
	return <AutocompletePrimitive.Backdrop data-slot='autocomplete-backdrop' {...props} />;
}

function AutocompletePositioner({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Positioner>>) {
	return (
		<AutocompletePrimitive.Positioner
			data-slot='autocomplete-positioner'
			className={css(s.positioner, ...styles)}
			{...props}
		/>
	);
}

function AutocompletePopup({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Popup>>) {
	return <AutocompletePrimitive.Popup data-slot='autocomplete-popup' className={css(s.popup, ...styles)} {...props} />;
}

function AutocompleteList({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.List>>) {
	return <AutocompletePrimitive.List data-slot='autocomplete-list' className={css(s.list, ...styles)} {...props} />;
}

function AutocompleteCollection({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Collection>) {
	return <AutocompletePrimitive.Collection data-slot='autocomplete-collection' {...props} />;
}

function AutocompleteRow({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Row>>) {
	return <AutocompletePrimitive.Row data-slot='autocomplete-row' className={css(s.row, ...styles)} {...props} />;
}

function AutocompleteItem({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Item>>) {
	return <AutocompletePrimitive.Item data-slot='autocomplete-item' className={css(s.item, ...styles)} {...props} />;
}

export interface AutocompleteContentProps
	extends AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Popup>> {
	align?: AutocompletePrimitive.Positioner.Props['align'];
	sideOffset?: AutocompletePrimitive.Positioner.Props['sideOffset'];
	alignOffset?: AutocompletePrimitive.Positioner.Props['alignOffset'];
	side?: AutocompletePrimitive.Positioner.Props['side'];
	showBackdrop?: boolean;
	anchor?: React.ComponentProps<typeof AutocompletePrimitive.Positioner>['anchor'];
}

function AutocompleteContent({
	styles = [],
	children,
	showBackdrop = false,
	align = 'start',
	sideOffset = 4,
	alignOffset = 0,
	side = 'bottom',
	anchor,
	...props
}: AutocompleteContentProps) {
	return (
		<AutocompletePortal>
			{showBackdrop && <AutocompleteBackdrop />}
			<AutocompletePositioner
				align={align}
				sideOffset={sideOffset}
				alignOffset={alignOffset}
				side={side}
				anchor={anchor}
			>
				<AutocompletePopup styles={styles} {...props}>
					{children}
				</AutocompletePopup>
			</AutocompletePositioner>
		</AutocompletePortal>
	);
}

function AutocompleteGroup({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Group>) {
	return <AutocompletePrimitive.Group data-slot='autocomplete-group' {...props} />;
}

function AutocompleteGroupLabel({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.GroupLabel>>) {
	return (
		<AutocompletePrimitive.GroupLabel
			data-slot='autocomplete-group-label'
			className={css(s.groupLabel, ...styles)}
			{...props}
		/>
	);
}

function AutocompleteEmpty({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Empty>>) {
	return <AutocompletePrimitive.Empty data-slot='autocomplete-empty' className={css(s.empty, ...styles)} {...props} />;
}

function AutocompleteClear({
	styles = [],
	children,
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Clear>>) {
	return (
		<AutocompletePrimitive.Clear data-slot='autocomplete-clear' className={css(s.clear, ...styles)} {...props}>
			{children ? children : <XIcon />}
		</AutocompletePrimitive.Clear>
	);
}

function AutocompleteArrow({ ...props }: React.ComponentProps<typeof AutocompletePrimitive.Arrow>) {
	return <AutocompletePrimitive.Arrow data-slot='autocomplete-arrow' {...props} />;
}

function AutocompleteSeparator({
	styles = [],
	...props
}: AphroditeStyled<React.ComponentProps<typeof AutocompletePrimitive.Separator>>) {
	return (
		<AutocompletePrimitive.Separator
			data-slot='autocomplete-separator'
			className={css(s.separator, ...styles)}
			{...props}
		/>
	);
}

export {
	Autocomplete,
	AutocompleteArrow,
	AutocompleteBackdrop,
	AutocompleteClear,
	AutocompleteCollection,
	AutocompleteContent,
	AutocompleteControl,
	AutocompleteEmpty,
	AutocompleteGroup,
	AutocompleteGroupLabel,
	AutocompleteIcon,
	AutocompleteInput,
	AutocompleteItem,
	AutocompleteList,
	AutocompletePopup,
	AutocompletePortal,
	AutocompletePositioner,
	AutocompleteRow,
	AutocompleteSeparator,
	AutocompleteStatus,
	AutocompleteTrigger,
	AutocompleteValue,
};
